import { SetStateAction, useEffect, useMemo, useState } from "react";
import axios from "axios";
import moment from "moment-timezone";
import "moment/locale/sv";
import { useTranslation } from "react-i18next";
import { TimeReportForStamping, TimeReportsForStamping } from "model/Company";
import { StampClockTeam } from "model/OrganizationalStructure";
import { TimeReportDto } from "model/TimeReport";
import { isSettingEnabled } from "model/settings.logic";
import { Api } from "services/api/api.service";
import { useAlertStore } from "stores/alertStore/alertStore";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import {
  buildDefaultStampSubmitData,
  type ClockStampSubmitData,
} from "./clockStampTimeReportPanel.logic";
import type { NumberPadInput } from "./numberPad.component";

const describeRequestError = (err: unknown): string =>
  axios.isAxiosError(err)
    ? `${err.response?.status ?? "no status"}: ${err.message}`
    : String(err);

export type StampResult = {
  checkInTime: string | null;
  checkOutTime: string | null;
};

const DEFAULT_TELEPHONE_PREFIX = "+46";
const SWEDISH_PHONE_REGEX = /^\+46\d{9}$/;
const MIN_PREFIX_LENGTH = 2;

export type ClockStampModalState =
  | { type: "none" }
  | { type: "checkInOut" }
  | { type: "createAutoGeneratedGig" }
  | { type: "chooseTimeReport" }
  | { type: "timeReport"; showWarningModal: boolean };

const toTimeReportForStamping = (
  reports: TimeReportsForStamping,
): TimeReportForStamping => {
  const firstTimeReport = reports.timeReports[0];
  return {
    ...firstTimeReport,
    companyId: reports.companyId,
    workerPhoneNumber: reports.workerPhoneNumber,
  };
};

export const useClockStamp = () => {
  const { t, i18n } = useTranslation();
  const [companyState, companyActions] = useCompanyStore();
  const [, , alertDispatch] = useAlertStore();

  const [telephoneNumber, setTelephoneNumber] = useState<string>(
    DEFAULT_TELEPHONE_PREFIX,
  );
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [userIsCheckedIn, setUserIsCheckedIn] = useState<boolean>(false);
  const [keyShouldBeDisabled, setKeyShouldBeDisabled] =
    useState<boolean>(false);
  const [modalState, setModalState] = useState<ClockStampModalState>({
    type: "none",
  });
  const [today, setToday] = useState<string>("");
  const [time, setTime] = useState<string>("");
  const [selectedWorkerTimeReport, setSelectedWorkerTimeReport] =
    useState<TimeReportForStamping>();
  const [workerTimeReports, setWorkerTimeReports] =
    useState<TimeReportsForStamping | null>(null);
  const [errorText, setErrorText] = useState("");
  const [isAutoGeneratedGig, setIsAutoGeneratedGig] = useState(false);
  const [stampResult, setStampResult] = useState<StampResult | null>(null);
  const [timeReportDetails, setTimeReportDetails] =
    useState<TimeReportDto | null>(null);
  const [teams, setTeams] = useState<StampClockTeam[]>([]);
  const [isTimeReportLoading, setIsTimeReportLoading] = useState(false);
  const [isSubmittingTimeReport, setIsSubmittingTimeReport] = useState(false);

  const setSimpleModalOpen =
    (modalType: "checkInOut" | "createAutoGeneratedGig" | "chooseTimeReport") =>
    (value: SetStateAction<boolean>) => {
      setModalState((prev) => {
        const isOpen = prev.type === modalType;
        const nextIsOpen = typeof value === "function" ? value(isOpen) : value;

        if (nextIsOpen) {
          return { type: modalType };
        }

        if (isOpen) {
          return { type: "none" };
        }

        return prev;
      });
    };

  const setTimeReportModalOpen = (value: SetStateAction<boolean>) => {
    setModalState((prev) => {
      const isOpen = prev.type === "timeReport";
      const nextIsOpen = typeof value === "function" ? value(isOpen) : value;

      if (nextIsOpen) {
        return { type: "timeReport", showWarningModal: false };
      }

      if (isOpen) {
        return { type: "none" };
      }

      return prev;
    });
  };

  const setWarningModalOpen = (value: SetStateAction<boolean>) => {
    setModalState((prev) => {
      if (prev.type !== "timeReport") {
        return prev;
      }

      const nextIsOpen =
        typeof value === "function" ? value(prev.showWarningModal) : value;

      return { ...prev, showWarningModal: nextIsOpen };
    });
  };

  const updateTime = () => {
    const lang = (i18n.language || "sv").split("-")[0];
    const currentDate = moment().tz("Europe/Stockholm").locale(lang);

    setTime(currentDate.format("HH:mm"));
    setToday(
      currentDate.format("dddd D MMMM").replace(/^\w/, (c) => c.toUpperCase()),
    );
  };

  const clearInputs = () => {
    setTelephoneNumber(DEFAULT_TELEPHONE_PREFIX);
    setErrorText("");
    setSelectedWorkerTimeReport(undefined);
    setKeyShouldBeDisabled(false);
  };

  const onClickedC = () => {
    setTelephoneNumber(DEFAULT_TELEPHONE_PREFIX);
    setErrorText("");
    setKeyShouldBeDisabled(false);
  };

  const showTimeReportErrorAlert = () => {
    alertDispatch({
      type: "SHOW_ALERT",
      payload: {
        icon: "alert",
        title: t("Alerts.OhNo"),
        message: t("Alerts.SomethingWentWrong"),
      },
    });
  };

  const fetchStampClockTimeReport = async (
    companyId: number,
    workerPhoneNumber: string,
    timeReportId: number,
  ): Promise<TimeReportDto | null> => {
    try {
      const res = await Api().company.getStampClockTimeReport(
        companyId,
        workerPhoneNumber,
        timeReportId,
      );
      return res.data;
    } catch (err) {
      console.error("Error fetching time report:", describeRequestError(err));
      return null;
    }
  };

  const doTheCheckInAndOut = async (
    companyId: number,
    timeReportId: number,
    workerPhoneNumber: string,
  ): Promise<void> => {
    try {
      const res = await companyActions.handleCheckInCheckOut(
        companyId,
        timeReportId,
        workerPhoneNumber,
      );
      const updated = res?.active;
      setStampResult(
        updated
          ? {
              checkInTime: updated.checkInTime ?? null,
              checkOutTime: updated.checkOutTime ?? null,
            }
          : null,
      );
      setModalState({ type: "checkInOut" });
      setTelephoneNumber(DEFAULT_TELEPHONE_PREFIX);
      setKeyShouldBeDisabled(false);

      // The check-out modal previews the values the time report will be
      // submitted with, so the report is loaded as soon as it is stamped out.
      if (updated?.checkOutTime) {
        setIsTimeReportLoading(true);
        const fetchedTimeReport = await fetchStampClockTimeReport(
          companyId,
          workerPhoneNumber,
          timeReportId,
        );
        setTimeReportDetails(fetchedTimeReport);
        setIsTimeReportLoading(false);
        if (!fetchedTimeReport) {
          showTimeReportErrorAlert();
        }
      }
    } catch {
      console.log("problem checkin and out");
    }
  };

  const handleCheckInCheckOut = () => {
    setSimpleModalOpen("chooseTimeReport")(false);
    if (!selectedWorkerTimeReport) return;

    const {
      timeReportId,
      checkInTime,
      checkOutTime,
      companyId,
      workerPhoneNumber,
    } = selectedWorkerTimeReport;

    const phoneOk = workerPhoneNumber === telephoneNumber.substring(1);
    const hasOpenSide = !checkInTime || !checkOutTime;
    if (!timeReportId || !companyId || !phoneOk || !hasOpenSide) return;

    doTheCheckInAndOut(companyId, timeReportId, workerPhoneNumber);
  };

  const timeReportDefaultSubmitData = useMemo(
    () =>
      timeReportDetails
        ? buildDefaultStampSubmitData(
            timeReportDetails,
            companyState.companySettings,
          )
        : null,
    [timeReportDetails, companyState.companySettings],
  );

  const handleEditTimeReport = () => {
    if (!timeReportDetails) return;
    setTimeReportModalOpen(true);
  };

  const submitTimeReport = async (data: ClockStampSubmitData) => {
    if (isSubmittingTimeReport || !timeReportDetails?.id) return;

    const workerPhoneNumber = selectedWorkerTimeReport?.workerPhoneNumber;
    const companyId = companyState.company?.id;
    if (!workerPhoneNumber || !companyId) return;

    setIsSubmittingTimeReport(true);
    try {
      await Api().company.clockStampTimeReport(
        companyId,
        workerPhoneNumber,
        timeReportDetails.id,
        data,
      );
      setIsSubmittingTimeReport(false);
      alertDispatch({
        type: "SHOW_ALERT",
        payload: {
          title: t("WorkerTimeReport.TimeReported"),
          message: "",
        },
      });
      setModalState({ type: "none" });
      setTimeReportDetails(null);
      setPhoneNumber("");
      clearInputs();
    } catch (err) {
      console.error("Error submitting time report:", describeRequestError(err));
      setIsSubmittingTimeReport(false);
      showTimeReportErrorAlert();
    }
  };

  const handleSubmitDefaultTimeReport = async () => {
    if (!timeReportDefaultSubmitData) return;
    await submitTimeReport(timeReportDefaultSubmitData);
  };

  const getWorkerTimeReport = async () => {
    const phoneSnapshot = (telephoneNumber || "").trim();
    const isValidNumber = SWEDISH_PHONE_REGEX.test(phoneSnapshot);
    const companyId = companyState.company?.id;
    if (!isValidNumber || !companyId) return;

    setPhoneNumber("");
    setModalState((prev) => {
      if (prev.type === "chooseTimeReport" || prev.type === "checkInOut") {
        return { type: "none" };
      }
      return prev;
    });
    setIsAutoGeneratedGig(false);
    setSelectedWorkerTimeReport(undefined);
    setTimeReportDetails(null);
    setStampResult(null);
    setWorkerTimeReports(null);
    setUserIsCheckedIn(false);

    let shouldOpenAutoGig = false;

    try {
      const res = await companyActions.getAvailableTimeReportsForStamping(
        companyId,
        phoneSnapshot.substring(1),
      );
      setWorkerTimeReports(res);
    } catch (err: any) {
      const msg = err?.response?.data?.errorMessage;

      if (msg === "No matching time report found") {
        try {
          const [allowGenerateRes, teamsRes] = await Promise.all([
            Api().company.getSettingForAllowingAutoGeneratedGig(companyId),
            Api().company.getStampClockWorkerWorkgroups(
              companyId,
              phoneSnapshot.substring(1),
            ),
          ]);
          setTeams(teamsRes.data);
          if (allowGenerateRes.data && teamsRes.data.length >= 1) {
            shouldOpenAutoGig = true;
          } else {
            setErrorText(t("StampClock.NoOpenTimeReports"));
          }
        } catch (e) {
          console.error("Failed to check auto-generate setting:", e);
          setErrorText(t("StampClock.NoOpenTimeReports"));
        }
      } else if (msg === "Worker not found") {
        setErrorText(t("StampClock.NoNumber"));
      } else if (msg === "Invalid or missing ssn") {
        setErrorText("StampClock.InvalidOrMissingSSN");
      } else {
        setErrorText(t("StampClock.NoOpenTimeReports"));
      }
    } finally {
      setPhoneNumber(phoneSnapshot);

      if (shouldOpenAutoGig) {
        setModalState({ type: "createAutoGeneratedGig" });
      }
    }
  };

  const handleNumberPadInput = (input: NumberPadInput) => {
    if (input.type === "clear") {
      onClickedC();
      return;
    }

    setTelephoneNumber(telephoneNumber + input.value);
  };

  useEffect(() => {
    updateTime();
    const intervalId = setInterval(updateTime, 1000);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const isValidNumber = SWEDISH_PHONE_REGEX.test(telephoneNumber);

    if (telephoneNumber.length >= MIN_PREFIX_LENGTH) {
      setErrorText("");
    }

    if (isValidNumber) {
      setErrorText("");
      getWorkerTimeReport();
    }
  }, [telephoneNumber, companyState.company?.id]);

  useEffect(() => {
    if (workerTimeReports) {
      if (workerTimeReports.timeReports.length > 1) {
        setSelectedWorkerTimeReport(undefined);
        setModalState({ type: "chooseTimeReport" });
      } else {
        setSelectedWorkerTimeReport(toTimeReportForStamping(workerTimeReports));
      }
    }
  }, [workerTimeReports]);

  useEffect(() => {
    if (selectedWorkerTimeReport) {
      setUserIsCheckedIn(
        !!(
          selectedWorkerTimeReport.checkInTime &&
          !selectedWorkerTimeReport.checkOutTime
        ),
      );
    } else {
      setUserIsCheckedIn(false);
    }
    setKeyShouldBeDisabled(
      SWEDISH_PHONE_REGEX.test(telephoneNumber) && !!selectedWorkerTimeReport,
    );
  }, [selectedWorkerTimeReport, telephoneNumber]);

  const haveStampClockModule = useMemo(() => {
    return isSettingEnabled(
      companyState.companySettings,
      "CompanyShowStampclockModule",
    );
  }, [companyState.companySettings]);

  return {
    t,
    telephoneNumber,
    setTelephoneNumber,
    userIsCheckedIn,
    keyShouldBeDisabled,
    modalState,
    today,
    time,
    selectedWorkerTimeReport,
    setSelectedWorkerTimeReport,
    workerTimeReports,
    errorText,
    isAutoGeneratedGig,
    setIsAutoGeneratedGig,
    timeReportDetails,
    setTimeReportDetails,
    stampResult,
    teams,
    timeReportDefaultSubmitData,
    isTimeReportLoading,
    isSubmittingTimeReport,
    phoneNumber,
    setPhoneNumber,
    setKeyShouldBeDisabled,
    setSimpleModalOpen,
    setTimeReportModalOpen,
    setWarningModalOpen,
    clearInputs,
    handleCheckInCheckOut,
    handleEditTimeReport,
    handleSubmitDefaultTimeReport,
    submitTimeReport,
    handleNumberPadInput,
    haveStampClockModule,
  };
};
